import { startSpan } from './monitoring.js';
import type { MonitoringSpan } from './monitoring-types.js';
import type { SpanAttrs } from './trace-types';

type Span = MonitoringSpan;

function applyAttrs(span: Span, attrs?: SpanAttrs): void {
  if (!attrs) return;
  for (const key of Object.keys(attrs)) {
    const value = attrs[key];
    if (value === undefined) continue;
    span.setAttribute(key, value);
  }
}

function isThenable(value: unknown): value is Promise<unknown> {
  return !!value && typeof (value as { then?: unknown }).then === 'function';
}

export function trace<T>(name: string, fn: () => T, attrs?: SpanAttrs): T {
  const span = startSpan(name);
  applyAttrs(span, attrs);

  let result: T;
  try {
    result = fn();
  } catch (err) {
    span.end();
    throw err;
  }

  if (isThenable(result)) {
    return (result as unknown as Promise<unknown>).finally(() => span.end()) as unknown as T;
  }

  span.end();
  return result;
}

export function Trace(name?: string, attrs?: SpanAttrs): MethodDecorator {
  return function (target, propertyKey, descriptor: PropertyDescriptor): PropertyDescriptor {
    const original = descriptor.value;
    if (typeof original !== 'function') return descriptor;

    const ctorName = (target as { constructor?: { name?: string } }).constructor?.name ?? 'anonymous';
    const spanName = name ?? `${ctorName}.${String(propertyKey)}`;

    descriptor.value = function (this: unknown, ...args: unknown[]) {
      return trace(spanName, () => original.apply(this, args), attrs);
    };

    return descriptor;
  };
}
